import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/styles/premium-effects.css";
// Feb-2026 speed pass: install the fetch-dedupe wrapper BEFORE any
// component mounts so the very first `useEffect` fetch calls are
// already going through the shared-promise layer.
import "@/lib/memberToken";
import "@/lib/fetchDedupe";
import App from "@/App";

if (localStorage.getItem("vaad_theme") === "dark") {
  document.documentElement.classList.add("dark");
}

// Apply the persisted text-scale setting BEFORE React mounts so users on
// KaiOS / feature phones don't see a flash of the default size first.
try {
  const raw = localStorage.getItem("vaad_settings_v1");
  if (raw) {
    const parsed = JSON.parse(raw);
    const scale = parsed && parsed.textScale;
    if (scale === "large") document.documentElement.classList.add("vaad-text-large");
    else if (scale === "huge") document.documentElement.classList.add("vaad-text-huge");
  }
} catch (_) { /* ignore */ }

// Debounce ResizeObserver callbacks with rAF to avoid the "loop completed
// with undelivered notifications" warning that fires when observers mutate
// layout during their callbacks (React Flow's MiniMap is a common source).
if (typeof window !== "undefined" && window.ResizeObserver) {
  const _RO = window.ResizeObserver;
  window.ResizeObserver = class extends _RO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          try { callback(entries, observer); } catch (_) { /* ignore */ }
        });
      });
    }
  };
}

// Silence the harmless "ResizeObserver loop completed with undelivered
// notifications" warning that React Flow / MiniMap can trigger. It's a
// benign browser diagnostic (spec-defined behaviour) but CRA's dev
// overlay treats every window "error" event as fatal and covers the UI.
const RO_LOOP = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
window.addEventListener("error", (e) => {
  if (e.message && RO_LOOP.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = e.reason && (e.reason.message || String(e.reason));
  if (msg && RO_LOOP.test(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// Feb 2026 (user "Loading chunk … failed"): when a lazy chunk 404s
// (because the browser is running an old bundle.js that references
// hashed chunk names from a previous build), auto-clear caches and
// hard-reload once so the user picks up the fresh bundle without
// needing to manually clear their browser cache. We store a flag in
// sessionStorage so the reload only happens once per session — if it
// keeps failing, we don't loop.
const CHUNK_ERR_RE = /Loading chunk .* failed|ChunkLoadError|Loading CSS chunk .* failed/;
const RELOAD_FLAG = "vaad-chunk-reload";
const chunkErrorHandler = (msg) => {
  if (!msg || !CHUNK_ERR_RE.test(msg)) return false;
  if (sessionStorage.getItem(RELOAD_FLAG)) return false;
  sessionStorage.setItem(RELOAD_FLAG, "1");
  // Nuke SW caches so the next load doesn't hit the old shell.
  (async () => {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if (navigator.serviceWorker) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (_) { /* best effort */ }
    window.location.reload();
  })();
  return true;
};
window.addEventListener("error", (e) => {
  chunkErrorHandler(e.message);
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = e.reason && (e.reason.message || String(e.reason));
  chunkErrorHandler(msg);
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the Vaad Otzar Service Worker. It caches immutable portrait
// bytes and stale-while-revalidates the speakers/recordings JSON so
// return visits are 0-network on preview (the CDN there strips our
// Cache-Control header, which is why preview felt slower than the
// production site on thevaadotzar.com).
//
// Auto-update policy (Feb-2026 v2 — user "im extremely frustrated
// there are many updates you did that do not show on mobile … tell me
// why this happens that it always is fine on desktop but almost never
// on mobile"). ROOT CAUSE was FOUR separate mobile-specific bugs, all
// fixed below:
//
//   1. `updateViaCache: 'none'` — MOST IMPORTANT. Without this, iOS
//      Safari and mobile Chrome HTTP-cache the `/sw.js` file itself
//      for up to 24 hours. Every `registration.update()` returns the
//      SAME cached SW file, so the browser never notices we bumped
//      `SW_VERSION` from v217 → v218 → v219 → … Setting `none` forces
//      the browser to re-fetch sw.js from the server on every update
//      check, which is what lets the version bump actually land.
//   2. Update check on `visibilitychange` — mobile browsers throttle
//      `setInterval` to a crawl (or freeze it entirely) once the tab
//      goes into the background, which on iOS happens on ANY app
//      switch. Firing an update the instant the tab becomes visible
//      catches every return-to-app.
//   3. Update check on `focus` and `pageshow` — pageshow especially,
//      because iOS Safari fires it when the user navigates back to a
//      bfcache-restored page (very common on mobile).
//   4. HTML `<meta http-equiv="Cache-Control" content="no-cache">` in
//      index.html — mobile browsers otherwise cache the shell HTML for
//      hours, serving old <script src=/static/js/main.<hash>.js> tags
//      that point to bundles the server no longer has (chunk-load
//      error handler above catches these but only on Nth reload).
//
// The old `controllerchange → reload` behaviour is preserved so the
// fresh JS bundle takes effect immediately once the new SW activates.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      // Fix #1: force browser to re-download /sw.js on every update
      // check. This is the critical mobile fix.
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        // Poll for updates every 60s while the tab is open. Cheap —
        // 304 if nothing changed. Kept as a floor for very long
        // sessions that never trigger visibility/focus.
        setInterval(() => {
          registration.update().catch(() => {});
        }, 60 * 1000);

        // Fix #2/#3: also check for updates whenever the tab becomes
        // visible or the window regains focus. On mobile this fires
        // every single time the user returns to the app — exactly the
        // moment they'd expect fresh content to appear.
        const forceUpdate = () => { registration.update().catch(() => {}); };
        document.addEventListener("visibilitychange", () => {
          if (!document.hidden) forceUpdate();
        });
        window.addEventListener("focus", forceUpdate);
        window.addEventListener("pageshow", (e) => {
          // `persisted=true` means bfcache restore (iOS Safari back).
          if (e.persisted) forceUpdate();
        });
      })
      .catch((err) => console.warn("SW registration failed:", err));

    // When a new SW takes control (i.e. after activation), reload once
    // so the page's cached HTML/JS is refreshed with the new bundle —
    // but NEVER while a listener has a recording loaded (a publish used to
    // cut off every active listener within 60 s — and a background stall
    // used to trigger the deferred reload the instant audio paused, which
    // is exactly when the player is trying to recover). Deferred reloads
    // run once the player is closed AND the tab is hidden.
    let hasReloaded = false;
    let reloadPending = false;
    const tryReload = () => {
      if (hasReloaded) return;
      if (window.__vaadAudioPlaying || window.__vaadPlayerActive) { reloadPending = true; return; }
      hasReloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", tryReload);
    const flushPending = () => { if (reloadPending && document.hidden) tryReload(); };
    document.addEventListener("visibilitychange", flushPending);
    window.addEventListener("vaad:audio-stopped", flushPending);
  });
}
